import React from 'react';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useSdk } from '../../sdkContext';
import { denormalisedResponseEntities } from '../../util/data';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import SubscriptionSummary from './SubscriptionSummary';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
} from '../../components';
import { TopbarContainer } from '../../containers';
import CheckoutForm from './CheckoutForm';

import css from './SubscriptionCheckoutPage.css';

const CheckoutContainer = (props) => {
  const { intl, params } = props;
  const sdk = useSdk();
  const history = useHistory();

  const listingId = params.subId;
  const currentUser = useSelector(state => state.user.currentUser);
  
  const {
    data: listing,
    isLoading: isListingLoading,
    isError: isListingError
  } = useQuery(
    ['subscriptionListing', listingId],
    async () => {
      const response = await sdk.newSdk.subscriptionListings.show({
        id: listingId,
        include: ['provider', 'images', 'provider.deliveryToProviders'],
      });
      return denormalisedResponseEntities(response)[0];
    },
    {
      enabled: !!listingId,
      staleTime: 1000 * 60 * 5,
    }
  );

  const { mutate: createSubscription, isLoading: isSubmitting, isError: isCreateError } = useMutation(
    async (formData) => {
      const response = await sdk.newSdk.subscriptions.create({
        subscriptionListingId: listingId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        meta: formData.meta,
        ...formData.deliveryAttr
      });
      return response.data;
    },
    {
      onSuccess: (response) => {
        const newSubscriptionId = response.data.id.uuid;
        history.push(`/subpayment/${newSubscriptionId}`);
      },
      onError: (error) => {
        alert(error?.message || 'Błąd tworzenia subskrypcji');
      }
    }
  );

  const handleNextStep = (formData) => {
    createSubscription(formData);
  };

  if (isListingLoading) {
    return <div className="loading-state">Pobieranie informacji o produkcie...</div>;
  }

  if (isListingError || !listing) {
    return <div className="error-state">Nie udało się załadować szczegółów produktu.</div>;
  }

  const userAttrs = currentUser?.attributes|| {};

  const initialValues = {
    firstName: userAttrs.firstName || '',
    lastName: userAttrs.lastName || '',
    email: userAttrs.email || '',
    invoiceType: userAttrs.defaultTransactionMeta?.customerType || 'individual',
    invoice: {
      city: userAttrs.defaultTransactionMeta?.city || '',
      zip: userAttrs.defaultTransactionMeta?.zip || '',
      address: userAttrs.defaultTransactionMeta?.address || '',
      companyName: userAttrs.defaultTransactionMeta?.companyName || '',
      taxId: userAttrs.defaultTransactionMeta?.taxId || '',
      name: userAttrs.defaultTransactionMeta?.name || ''
    }
  };

  return (
    <Page title={intl.formatMessage({ id: 'CheckoutPage.title' })}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <div className={css.container}>
            <div className={css.content}>
              <div className={css.layoutWrapper}>
                <div className={css.formColumn}>
                  <CheckoutForm
                    initialValues={initialValues}
                    onSubmit={handleNextStep}
                    isSubmitting={isSubmitting}
                    subscriptionTerms={listing.provider.attributes.subscriptionTerms}
                    provider={listing.provider}
                  />
                  {isCreateError && (
                    <div className={css.errorMessage}>
                      <FormattedMessage id="CheckoutPage.errorCreate" />
                    </div>
                  )}
                </div>
                <div className={css.summaryColumn}>
                  <SubscriptionSummary
                    currentListing={listing}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

export default compose(injectIntl)(CheckoutContainer);